import { FC } from "react";
import {
  InvoiceTransaction,
  PAYMENT_METHOD,
} from "modules/invoice/types/invoice.interface";

interface TransactionHistoryProps {
  transactions: InvoiceTransaction[];
  totalInvoiceAmount: number;
}

export const TransactionHistory: FC<TransactionHistoryProps> = ({
  transactions,
  totalInvoiceAmount,
}) => {
  const formatPaymentMethod = (method: PAYMENT_METHOD): string => {
    switch (method) {
      case PAYMENT_METHOD.BANK_TRANSFER:
        return "Virement bancaire";
      case PAYMENT_METHOD.CREDIT_CARD:
        return "Carte bancaire";
      case PAYMENT_METHOD.CHECK:
        return "Chèque";
      case PAYMENT_METHOD.CASH:
        return "Espèces";
      case PAYMENT_METHOD.OTHER:
        return "Autre";
      default:
        return method;
    }
  };

  const getTotalPaid = (transactions: InvoiceTransaction[]): number => {
    return transactions.reduce(
      (total, transaction) => total + transaction.amount,
      0
    );
  };

  const totalPaid = getTotalPaid(transactions);
  const remainingBalance = totalInvoiceAmount - totalPaid;
  const isPaidInFull = remainingBalance <= 0;

  return (
    <div>
      <div className="flex items-center justify-between"></div>

      <div className="mb-5 grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-4">
        <div className="text-sm font-medium text-gray-600">Total facture</div>
        <div className="text-right text-sm">
          {totalInvoiceAmount.toFixed(2)} €
        </div>

        <div className="text-sm font-medium text-gray-600">Total payé</div>
        <div className="text-right text-sm">{totalPaid.toFixed(2)} €</div>

        <div className="text-sm font-medium text-gray-600">Reste à payer</div>
        <div className="text-right text-sm font-semibold">
          {remainingBalance > 0 ? `${remainingBalance.toFixed(2)} €` : "0.00 €"}
        </div>

        <div className="text-sm font-medium text-gray-600">Statut</div>
        <div className="text-right text-sm">
          {isPaidInFull ? (
            <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
              Payée intégralement
            </span>
          ) : (
            <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
              Partiellement payée
            </span>
          )}
        </div>
      </div>

      {transactions.length > 0 ? (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Date
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Méthode
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Référence
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Montant
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {transactions.map((transaction, index) => (
                <tr key={transaction._id || index}>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {transaction.paymentDate
                      ? new Date(transaction.paymentDate).toLocaleDateString(
                          "fr-FR"
                        )
                      : "Invalid Date"}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {formatPaymentMethod(transaction.paymentMethod)}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {transaction.reference || "-"}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                    {transaction.amount.toFixed(2)} €
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {transaction.notes || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mt-4 text-center text-gray-500">
          Aucun paiement enregistré pour cette facture.
        </div>
      )}
    </div>
  );
};
